// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Playfield paths into solid rails (M3-05).
//
// A machine states its guides as bare centre-lines: two points for a wall, a list for a
// polyline, a centre and two angles for an arc. The solver needs nothing more, because a wall
// is a line to a swept-circle test. A picture needs a body — a rail has a width, a height, two
// faces and two ends — and turning the one into the other is arithmetic, not rendering.
//
// So it lives here, with no Three.js in sight, and is tested in `node`. The whole class of
// mistake this module can make — a rail that pinches to nothing at a sharp corner, a face
// wound inside out and therefore invisible, a chain whose two halves do not meet — is
// arithmetic, and none of it needs a GPU to catch.
//
// Everything is in the playfield frame of PH-001: metres, +x right, +y up the playfield, +z
// out of the surface.

export type Point2 = readonly [number, number]

/**
 * The left-hand offset at each vertex of a path, as a **miter**.
 *
 * Offsetting each segment on its own leaves a wedge-shaped hole on the outside of every
 * corner and an overlap on the inside. A miter offsets each *vertex* instead, along the
 * bisector of the two adjacent segments, lengthened by `1 / cos(half-angle)` so the two
 * offset lines actually meet. That is what makes a chain of eleven points look like one
 * bent rail rather than eleven separate ones.
 *
 * The lengthening runs away at a hairpin — cos(half-angle) → 0 — so it is clamped. A clamped
 * miter leaves a small notch on the outside of a very sharp corner, which is a far better
 * failure than a spike shooting off across the playfield.
 */
export function miterOffsets(points: readonly Point2[], halfWidth: number): Point2[] {
  const n = points.length
  if (n < 2) throw new Error('miterOffsets: a path needs at least two points')

  const normals: Point2[] = []
  for (let i = 0; i < n - 1; i++) {
    const a = points[i]!
    const b = points[i + 1]!
    const dx = b[0] - a[0]
    const dy = b[1] - a[1]
    const len = Math.hypot(dx, dy)
    if (len === 0) throw new Error(`miterOffsets: repeated point at index ${i}`)
    // Left-hand perpendicular, the same convention the solver uses for a segment's normal.
    normals.push([-dy / len, dx / len])
  }

  const out: Point2[] = []
  for (let i = 0; i < n; i++) {
    const before = normals[i - 1]
    const after = normals[i]
    if (before === undefined) {
      out.push([after![0] * halfWidth, after![1] * halfWidth])
      continue
    }
    if (after === undefined) {
      out.push([before[0] * halfWidth, before[1] * halfWidth])
      continue
    }
    let mx = before[0] + after[0]
    let my = before[1] + after[1]
    const m = Math.hypot(mx, my)
    // A true reversal — the path doubling back on itself — has no bisector at all. Fall back
    // to the incoming normal rather than dividing by zero.
    if (m < 1e-9) {
      out.push([before[0] * halfWidth, before[1] * halfWidth])
      continue
    }
    mx /= m
    my /= m
    const cos = mx * after[0] + my * after[1]
    const scale = halfWidth / Math.max(cos, MITER_LIMIT)
    out.push([mx * scale, my * scale])
  }
  return out
}

/** Below this the miter is clamped: about 137° of turn, sharper than any guide in a machine. */
const MITER_LIMIT = 0.35

/**
 * A path into the triangles of a rail: two faces, a top, and a cap at each end.
 *
 * Non-indexed on purpose. Three's `computeVertexNormals` averages across shared vertices, so
 * an indexed rail comes out with its top edge smeared into its sides — a guide that looks
 * like a length of hose. Non-indexed, the same call gives one normal per face, which is what
 * a folded steel guide actually has.
 *
 * The winding is the part worth stating: the left face's normal points along the path's
 * left-hand perpendicular, the right face's the other way, and the top's along +z. Get one
 * backwards and it does not glitch — it simply vanishes when you walk round it.
 */
export function railTriangles(
  points: readonly Point2[],
  halfWidth: number,
  base: number,
  height: number,
): Float32Array {
  const offsets = miterOffsets(points, halfWidth)
  const n = points.length
  const top = base + height

  // 2 faces × 2 triangles + 1 top × 2 triangles, per segment, plus 2 triangles per end cap.
  const triangles = (n - 1) * 6 + 4
  const out = new Float32Array(triangles * 9)
  let w = 0

  const push = (x: number, y: number, z: number): void => {
    out[w++] = x
    out[w++] = y
    out[w++] = z
  }
  const left = (i: number, z: number): void =>
    push(points[i]![0] + offsets[i]![0], points[i]![1] + offsets[i]![1], z)
  const right = (i: number, z: number): void =>
    push(points[i]![0] - offsets[i]![0], points[i]![1] - offsets[i]![1], z)

  /** One triangle, each corner named as (which side, which vertex, which height). */
  const tri = (
    fa: (i: number, z: number) => void,
    ia: number,
    za: number,
    fb: (i: number, z: number) => void,
    ib: number,
    zb: number,
    fc: (i: number, z: number) => void,
    ic: number,
    zc: number,
  ): void => {
    fa(ia, za)
    fb(ib, zb)
    fc(ic, zc)
  }

  for (let i = 0; i < n - 1; i++) {
    const j = i + 1
    // Left face, outward along +normal.
    tri(left, i, base, left, j, top, left, j, base)
    tri(left, i, base, left, i, top, left, j, top)
    // Right face, outward along −normal.
    tri(right, i, base, right, j, base, right, j, top)
    tri(right, i, base, right, j, top, right, i, top)
    // Top, upward.
    tri(left, i, top, right, i, top, right, j, top)
    tri(left, i, top, right, j, top, left, j, top)
  }

  // End caps, facing back down the path and on along it. Without them a rail is a tube open
  // at both ends, and the inside of the far end is visible straight through the near one.
  tri(left, 0, base, right, 0, top, left, 0, top)
  tri(left, 0, base, right, 0, base, right, 0, top)
  const e = n - 1
  tri(left, e, base, left, e, top, right, e, top)
  tri(left, e, base, right, e, top, right, e, base)

  return out
}

/**
 * An arc collider as a polyline, fine enough that the facets do not read as facets.
 *
 * The solver keeps the arc analytic — it is a distance-to-centre test and exact. Only the
 * picture is faceted, and only by `maxRadians`, so the rail is inside the true curve by
 * `r·(1 − cos(maxRadians/2))`: 8 µm on NEON NOVA's 130 mm shoulders at the default. A ball
 * bouncing off a shoulder that is 8 µm from where it is drawn is not a thing anyone can see.
 */
export function sampleArc(
  centre: Point2,
  radius: number,
  angleFrom: number,
  angleTo: number,
  maxRadians = 0.06,
): Point2[] {
  const sweep = angleTo - angleFrom
  const steps = Math.max(1, Math.ceil(Math.abs(sweep) / maxRadians))
  const out: Point2[] = []
  for (let i = 0; i <= steps; i++) {
    const a = angleFrom + (sweep * i) / steps
    out.push([centre[0] + radius * Math.cos(a), centre[1] + radius * Math.sin(a)])
  }
  return out
}
